import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AbstractComponent } from '@/Components/Abstract/PureComponent';
import Modal from '../Modal/Modal';
import { isMobileScreen } from '@/Utils';
import Spinner from '../Spinner/Spinner';
import { PasswordStep } from './PasswordStep';
import { FinishStep } from './FinishStep';
import { PreprocessingStep } from './PreprocessingStep';
import { c } from 'ttag';
var ContinueTitle;
(function (ContinueTitle) {
    ContinueTitle[ContinueTitle["Default"] = 0] = "Default";
    ContinueTitle[ContinueTitle["Generating"] = 1] = "Generating";
    ContinueTitle[ContinueTitle["Finish"] = 2] = "Finish";
})(ContinueTitle || (ContinueTitle = {}));
var Steps;
(function (Steps) {
    Steps["PreprocessingStep"] = "preprocessing-step";
    Steps["PasswordStep"] = "password-step";
    Steps["FinishStep"] = "finish-step";
})(Steps || (Steps = {}));
class PasswordWizard extends AbstractComponent {
    constructor(props) {
        super(props, props.application);
        this.nextStep = async () => {
            if (this.state.lockContinue || this.state.isContinuing) {
                return;
            }
            if (this.state.step === Steps.FinishStep) {
                this.dismiss();
                return;
            }
            if (this.state.step === Steps.PreprocessingStep) {
                this.setState({
                    step: Steps.PasswordStep,
                });
                return;
            }
            this.setState({
                isContinuing: true,
                showSpinner: true,
                continueTitle: ContinueTitle.Generating,
            });
            const valid = await this.validateCurrentPassword();
            if (!valid) {
                this.resetContinueState();
                return;
            }
            const success = await this.processPasswordChange();
            if (!success) {
                this.resetContinueState();
                return;
            }
            this.setState({
                isContinuing: false,
                showSpinner: false,
                continueTitle: ContinueTitle.Finish,
                step: Steps.FinishStep,
            });
        };
        this.dismiss = () => {
            if (this.state.processing) {
                this.application.alerts
                    .alert(c('B1.Account.Password.Error').t `Cannot close window until pending tasks are complete.`)
                    .catch(console.error);
            }
            else {
                this.props.dismissModal();
            }
        };
        this.handleCurrentPasswordInputChange = (currentPassword) => {
            this.setFormDataState({
                currentPassword,
            }).catch(console.error);
        };
        this.handleNewPasswordInputChange = (newPassword) => {
            this.setFormDataState({
                newPassword,
            }).catch(console.error);
        };
        this.handleNewPasswordConfirmationInputChange = (newPasswordConfirmation) => {
            this.setFormDataState({
                newPasswordConfirmation,
            }).catch(console.error);
        };
        this.setContinueEnabled = (enabled) => {
            this.setState({
                lockContinue: !enabled,
            });
        };
        this.nextStepFromPreprocessing = () => {
            if (this.state.lockContinue) {
                this.setState({
                    lockContinue: false,
                }, () => {
                    void this.nextStep();
                });
            }
            else {
                void this.nextStep();
            }
        };
        this.registerWindowUnloadStopper();
        const baseState = {
            formData: {},
            continueTitle: ContinueTitle.Default,
        };
        if (props.application.featuresController.isVaultsEnabled()) {
            this.state = {
                ...baseState,
                lockContinue: true,
                step: Steps.PreprocessingStep,
            };
        }
        else {
            this.state = {
                ...baseState,
                lockContinue: false,
                step: Steps.PasswordStep,
            };
        }
    }
    componentDidMount() {
        super.componentDidMount();
    }
    componentWillUnmount() {
        super.componentWillUnmount();
        window.onbeforeunload = null;
    }
    registerWindowUnloadStopper() {
        window.onbeforeunload = () => {
            return true;
        };
    }
    resetContinueState() {
        this.setState({
            showSpinner: false,
            continueTitle: ContinueTitle.Default,
            isContinuing: false,
        });
    }
    async validateCurrentPassword() {
        var _a;
        const currentPassword = this.state.formData.currentPassword;
        const newPass = this.state.formData.newPassword;
        if (!currentPassword || currentPassword.length === 0) {
            this.application.alerts.alert(c('B1.Account.Password.Error').t `Please enter your current password.`).catch(console.error);
            return false;
        }
        if (!newPass || newPass.length === 0) {
            this.application.alerts.alert(c('B1.Account.Password.Error').t `Please enter a new password.`).catch(console.error);
            return false;
        }
        if (newPass !== this.state.formData.newPasswordConfirmation) {
            this.application.alerts
                .alert(c('B1.Account.Password.Error').t `Your new password does not match its confirmation.`)
                .catch(console.error);
            this.setFormDataState({
                status: undefined,
            }).catch(console.error);
            return false;
        }
        if (!((_a = this.application.sessions.getUser()) === null || _a === void 0 ? void 0 : _a.email)) {
            this.application.alerts
                .alert(c('B1.Account.Password.Error').t `We don't have your email stored. Please sign out then log back in to fix this issue.`)
                .catch(console.error);
            this.setFormDataState({
                status: undefined,
            }).catch(console.error);
            return false;
        }
        const success = await this.application.validateAccountPassword(this.state.formData.currentPassword);
        if (!success) {
            this.application.alerts
                .alert(c('B1.Account.Password.Error').t `The current password you entered is not correct. Please try again.`)
                .catch(console.error);
        }
        return success;
    }
    async processPasswordChange() {
        await this.application.performDesktopTextBackup();
        this.setState({
            lockContinue: true,
            processing: true,
        });
        await this.setFormDataState({
            status: c('B1.Account.Password.Status').t `Processing encryption keys…`,
        });
        const newPassword = this.state.formData.newPassword;
        const response = await this.application.changePassword(this.state.formData.currentPassword, newPassword);
        const success = !response.error;
        this.setState({
            processing: false,
            lockContinue: false,
        });
        if (!success) {
            this.setFormDataState({
                status: c('B1.Account.Password.Status').t `Unable to process your password. Please try again.`,
            }).catch(console.error);
        }
        else {
            this.setState({
                formData: {
                    ...this.state.formData,
                    status: c('B1.Account.Password.Status').t `Successfully changed password.`,
                },
            });
        }
        return success;
    }
    async setFormDataState(formData) {
        return this.setState({
            formData: {
                ...this.state.formData,
                ...formData,
            },
        });
    }
    continueLabel() {
        switch (this.state.continueTitle) {
            case ContinueTitle.Generating:
                return c('B1.Account.Password.Action').t `Generating Keys...`;
            case ContinueTitle.Finish:
                return c('B1.Account.Password.Action').t `Finish`;
            case ContinueTitle.Default:
            default:
                return c('B1.Account.Password.Action').t `Continue`;
        }
    }
    render() {
        return (_jsx("div", { className: "sn-component h-full w-full md:h-auto md:w-auto", id: "password-wizard", children: _jsx(Modal, { title: c('B1.Account.Password.Title').t `Change Password`, close: this.dismiss, actions: [
                    {
                        label: c('B1.Account.Password.Action').t `Cancel`,
                        onClick: this.dismiss,
                        type: 'cancel',
                        mobileSlot: 'left',
                    },
                    {
                        label: this.state.continueTitle === ContinueTitle.Generating && isMobileScreen() ? (_jsx(Spinner, { className: "h-4 w-4" })) : (this.continueLabel()),
                        onClick: this.nextStep,
                        type: 'primary',
                        mobileSlot: 'right',
                        disabled: this.state.lockContinue,
                    },
                ], children: _jsxs("div", { className: "px-4.5 py-4", children: [this.state.step === Steps.PreprocessingStep && (_jsx(PreprocessingStep, { onContinue: this.nextStepFromPreprocessing, setContinueEnabled: this.setContinueEnabled })), this.state.step === Steps.PasswordStep && (_jsx(PasswordStep, { onCurrentPasswordChange: this.handleCurrentPasswordInputChange, onNewPasswordChange: this.handleNewPasswordInputChange, onNewPasswordConfirmationChange: this.handleNewPasswordConfirmationInputChange })), this.state.step === Steps.FinishStep && _jsx(FinishStep, {})] }) }) }));
    }
}
export default PasswordWizard;
